/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { EMPTY_STRING, isArrowNavigationKey, isEnterKey, isEscapeKey, isNotNil } from '@common/tech-shared';

import {
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  numberAttribute,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { isUndefined, uniqueId } from 'lodash-es';
import { debounceTime, filter, Subscription } from 'rxjs';
import { AriaLiveRegionComponent } from '../../aria-live-region/aria-live-region.component';
import { SearchFieldComponent } from '../search-field/search-field.component';
import { SearchResultHeaderComponent } from '../search-result-header/search-result-header.component';
import { SearchResultItemComponent } from '../search-result-item/search-result-item.component';
import { SearchResultLayerComponent, SearchResultLayerVariants } from '../search-result-layer/search-result-layer.component';
import { InstantSearchQuery } from './instant-search.model';

@Component({
  selector: 'ods-instant-search',
  imports: [SearchFieldComponent, SearchResultLayerComponent, AriaLiveRegionComponent, SearchResultHeaderComponent],
  host: { class: 'relative block' },
  template: `
    <ods-search-field
      [label]="label"
      [placeholder]="placeholder"
      [control]="fieldControl"
      [focusOnInput]="focusOnSearchField"
      [showLabel]="showLabel"
      [ariaExpanded]="itemsList?.length > 0 && areResultsVisible"
      [attr.ariaControls]="resultsId"
      (searchQueryCleared)="searchQueryCleared.emit()"
      #searchField
    />
    <ods-aria-live-region [text]="ariaLiveText" />
    @if (itemsList?.length && areResultsVisible) {
      <ods-search-result-layer [attr.id]="resultsId" [variant]="variant">
        @if (headerText) {
          <ods-search-result-header [text]="headerText" [count]="itemsList?.length" header />
        }
        <ng-content />
      </ods-search-result-layer>
    }
  `,
})
export class InstantSearchComponent implements OnInit, OnDestroy {
  readonly _ref = inject(ElementRef);
  readonly _cdr = inject(ChangeDetectorRef);

  @Input() searchResultItemList: QueryList<SearchResultItemComponent>;
  @Input() fieldControl: FormControl = new FormControl();
  @Input({ required: true }) label: string;
  @Input() placeholder: string = EMPTY_STRING;
  @Input() headerText: string = EMPTY_STRING;
  @Input({ transform: booleanAttribute }) focusOnSearchField: boolean = false;
  @Input({ transform: booleanAttribute }) showLabel: boolean = false;
  @Input({ transform: numberAttribute }) previewSearchMinLength: number = 2;
  @Input() variant: SearchResultLayerVariants['variant'];

  @Output() searchResultsClosed: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
  @Output() inputClicked: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
  @Output() searchQueryChanged: EventEmitter<InstantSearchQuery> = new EventEmitter<InstantSearchQuery>();
  @Output() searchQueryCleared: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  public readonly resultsId: string = uniqueId('search-results-');
  public readonly firstItemIndex = 0;
  readonly _debounceTime = 300;
  public areResultsVisible: boolean = true;
  _focusedResultIndex: number;
  _formControlSubscription: Subscription;

  @ViewChild('searchField') searchFieldRef: SearchFieldComponent;
  @ContentChildren(SearchResultItemComponent) contentItemsList: QueryList<SearchResultItemComponent>;

  ngOnInit(): void {
    this.listenValueChanges();
  }

  listenValueChanges() {
    this._formControlSubscription = this.fieldControl.valueChanges
      .pipe(
        debounceTime(this._debounceTime),
        filter((value: string) => value.length >= this.previewSearchMinLength),
      )
      .subscribe((searchBy: string) => this._handleValueChanges(searchBy));
  }

  _handleValueChanges(searchBy: string) {
    this.searchQueryChanged.emit({ searchBy });
    this._showResults();
  }

  @HostListener('document:click', ['$event'])
  onClickHandler(e: MouseEvent): void {
    if (!this._ref.nativeElement.contains(e.target)) this.hideResults();
  }

  @HostListener('keydown', ['$event'])
  onKeydownHandler(e: KeyboardEvent): void {
    if (isEscapeKey(e) || isEnterKey(e) || isArrowNavigationKey(e)) e.preventDefault();
    if (!this.itemsList?.length) return;

    if (isEscapeKey(e)) this._handleEscape();
    if (isEnterKey(e)) this._showResults();
    if (isArrowNavigationKey(e)) this._handleArrowNavigation(e);
  }

  _handleArrowNavigation(e: KeyboardEvent): void {
    this._showResults();
    this._cdr.detectChanges();
    this.setFocusOnResultItem(this.getResultIndexForKey(e.key));
  }

  private setFocusOnResultItem(index: number): void {
    this._focusedResultIndex = index;
    this.itemsList.get(index).setFocus();
  }

  _handleEscape(): void {
    this.hideResults();
    this.searchResultsClosed.emit();
  }

  getResultIndexForKey(key: string): number {
    switch (key) {
      case 'ArrowDown':
        return this.nextResultIndex;
      case 'ArrowUp':
        return this.previousResultIndex;
      default:
        console.error('Key %s not allowed', key);
    }
  }

  private get nextResultIndex(): number {
    if (isUndefined(this._focusedResultIndex) || this._focusedResultIndex == this.lastItemIndex) return this.firstItemIndex;
    else return this._focusedResultIndex + 1;
  }

  private get previousResultIndex(): number {
    if (isUndefined(this._focusedResultIndex) || this._focusedResultIndex === 0) return this.lastItemIndex;
    else return this._focusedResultIndex - 1;
  }

  private get lastItemIndex(): number {
    return this.itemsList?.length - 1;
  }

  get itemsList(): QueryList<SearchResultItemComponent> {
    return this.searchResultItemList ?? this.contentItemsList;
  }

  get ariaLiveText(): string {
    if (!this.areResultsVisible) return EMPTY_STRING;

    if (this.itemsList?.length === 1)
      return `Ein Suchergebnis für Eingabe ${this.fieldControl.value}. Nutze Pfeiltaste nach unten, um das zu erreichen.`;
    if (this.itemsList?.length > 1)
      return `${this.itemsList?.length} Suchergebnisse für Eingabe ${this.fieldControl.value}. Nutze Pfeiltaste nach unten, um diese zu erreichen.`;

    return 'Keine Ergebnisse';
  }

  _showResults(): void {
    this.areResultsVisible = true;
  }

  hideResults(): void {
    this.areResultsVisible = false;
    this._focusedResultIndex = undefined;
  }

  ngOnDestroy(): void {
    if (isNotNil(this._formControlSubscription)) this._formControlSubscription.unsubscribe();
  }
}
